import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Wifi, Car, Coffee, Dumbbell, Briefcase, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const UrbanSuites = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '1'
  });

  const handleBooking = (e) => {
    e.preventDefault();
    alert('This is a demo site. Booking functionality is not active.');
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 bg-orange-500 text-white p-3 rounded-full shadow-lg hover:bg-orange-600 transition-all duration-300 hover:scale-110"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      {/* Hero */}
      <header className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=1920&q=80"
            alt="Urban Hotel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/60 to-neutral-950/30"></div>
        </div>

        <div className="relative h-full flex flex-col">
          <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-orange-500" />
              <span className="text-2xl font-bold tracking-tight">URBAN SUITES</span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              <button onClick={() => document.getElementById('rooms').scrollIntoView({ behavior: 'smooth' })} className="hover:text-orange-500 transition-colors">ROOMS</button>
              <button onClick={() => document.getElementById('amenities').scrollIntoView({ behavior: 'smooth' })} className="hover:text-orange-500 transition-colors">AMENITIES</button>
              <Button onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="bg-orange-500 hover:bg-orange-600 px-6">BOOK NOW</Button>
            </div>
          </nav>

          <div className="flex-1 flex items-end pb-24 px-6">
            <div className="container mx-auto">
              <p className="text-orange-500 font-medium tracking-widest text-sm mb-4">GENEVA CITY CENTER</p>
              <h1 className="text-7xl md:text-9xl font-bold leading-none mb-6">
                STAY<br />
                <span className="text-orange-500">CENTRAL</span>
              </h1>
              <p className="text-xl text-neutral-400 max-w-lg">
                Modern boutique hotel in the heart of Geneva. Where business meets pleasure.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Rooms Section */}
      <section id="rooms" className="py-24 bg-neutral-900">
        <div className="container mx-auto px-6">
          <div className="mb-16">
            <p className="text-orange-500 font-medium tracking-widest text-sm mb-4">ACCOMMODATIONS</p>
            <h2 className="text-5xl font-bold">Our Rooms</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Studio Suite", price: "CHF 280", image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80", size: "28m²" },
              { name: "Executive Suite", price: "CHF 420", image: "https://images.unsplash.com/photo-1591088398332-8a7791972843?w=600&q=80", size: "42m²" },
              { name: "Penthouse Loft", price: "CHF 680", image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80", size: "65m²" }
            ].map((room, i) => (
              <div key={i} className="group">
                <div className="relative h-80 overflow-hidden mb-6">
                  <img src={room.image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-4 left-4 bg-black/80 px-4 py-2">
                    <span className="text-orange-500 font-bold">{room.price}</span>
                    <span className="text-neutral-400 text-sm"> / night</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">{room.name}</h3>
                <p className="text-neutral-500">{room.size} • City View • King Bed</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section id="amenities" className="py-24 bg-neutral-950">
        <div className="container mx-auto px-6">
          <div className="mb-16">
            <p className="text-orange-500 font-medium tracking-widest text-sm mb-4">SERVICES</p>
            <h2 className="text-5xl font-bold">What We Offer</h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-8">
            {[
              { icon: Wifi, name: "High-Speed WiFi" },
              { icon: Dumbbell, name: "24/7 Gym" },
              { icon: Coffee, name: "Rooftop Bar" },
              { icon: Briefcase, name: "Business Center" },
              { icon: Car, name: "Valet Parking" },
              { icon: Building2, name: "Concierge" }
            ].map((item, i) => (
              <div key={i} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 border border-neutral-700 flex items-center justify-center group-hover:border-orange-500 group-hover:bg-orange-500/10 transition-all duration-300">
                  <item.icon className="h-8 w-8 text-orange-500" />
                </div>
                <p className="text-sm text-neutral-400">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-orange-500">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-black font-medium tracking-widest text-sm mb-4">RESERVATIONS</p>
            <h2 className="text-5xl font-bold text-black">Book Your Stay</h2>
          </div>

          <form onSubmit={handleBooking} className="bg-black p-10">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div>
                <Label className="text-neutral-400 mb-2 block text-sm">CHECK-IN</Label>
                <Input type="date" value={bookingData.checkIn} onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})} className="bg-neutral-900 border-neutral-700 text-white h-12" required />
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-sm">CHECK-OUT</Label>
                <Input type="date" value={bookingData.checkOut} onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})} className="bg-neutral-900 border-neutral-700 text-white h-12" required />
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-sm">GUESTS</Label>
                <select value={bookingData.guests} onChange={(e) => setBookingData({...bookingData, guests: e.target.value})} className="w-full h-12 px-3 bg-neutral-900 border border-neutral-700 text-white rounded-md">
                  {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold h-12">
                  SEARCH
                </Button>
              </div>
            </div>
            <p className="text-center text-neutral-500 text-xs italic">* Demo site - booking not functional</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-neutral-950 border-t border-neutral-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center mb-12">
            <div><MapPin className="h-6 w-6 mx-auto mb-2 text-orange-500" /><p className="text-neutral-400">Rue du Rhône 42, Geneva</p></div>
            <div><Phone className="h-6 w-6 mx-auto mb-2 text-orange-500" /><p className="text-neutral-400">+41 22 818 0000</p></div>
            <div><Mail className="h-6 w-6 mx-auto mb-2 text-orange-500" /><p className="text-neutral-400">hello@urbansuites.ch</p></div>
          </div>
          <p className="text-center text-neutral-600 text-sm">© 2026 Urban Suites Geneva • Demo Site</p>
        </div>
      </footer>
    </div>
  );
};

export default UrbanSuites;
